import React, { useState, useEffect } from 'react';
import { Modal, Button, Input, Form, Typography, Space } from 'antd';
import { EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';
import GYAxios from '../../util/GYAxios';
import Noti from '../../util/noti';
import { useUser } from '../../util/user';
import { useRadioGroup } from '@material-ui/core';

const { Text } = Typography;

const KCPassResetPopup = () => {
  const user = useUser();
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordMismatchError, setPasswordMismatchError] = useState('');
  const [form] = Form.useForm();

  // Check if password reset is needed on component mount
  useEffect(() => {
    if(user) {
      checkShouldResetPassword();
    }
  }, [ user ]);

  const checkShouldResetPassword = async () => {
    try {
      const response = await GYAxios.get('/keycloak/should-reset-password');
      if (response.data.success && response.data.shouldReset) {
        setVisible(true);
      }
    } catch (error) {
      console.error('Error checking password reset requirement:', error);
    }
  };

  const validatePassword = (password) => {
    const minLength = 8;
    const hasUpper = /[A-Z]/.test(password);
    const hasLower = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecial = /[!@#$%^&*(),.?":{}|<>[\]\\\/;'+=_-]/.test(password);
    const hasNoSpaces = !/\s/.test(password);

    const errors = [];
    if (password.length < minLength) errors.push('At least 8 characters long');
    if (!hasUpper) errors.push('Contains uppercase letter');
    if (!hasLower) errors.push('Contains lowercase letter');
    if (!hasNumber) errors.push('Contains number');
    if (!hasSpecial) errors.push('Contains special character');
    if (!hasNoSpaces) errors.push('No spaces allowed');

    return {
      isValid: password.length >= minLength && hasUpper && hasLower && hasNumber && hasSpecial && hasNoSpaces,
      errors
    };
  };

  const handleResetPassword = async () => {
    try {
      await form.validateFields();
    } catch(err) {
      return;
    }
    try {   
      const validation = validatePassword(password);
      if (!validation.isValid) {
        Noti.create({
          type: "e",
          msg: `Password requirements not met: ${validation.errors.join(', ')}`
        });
        return;
      }

      if (password !== confirmPassword) {
        setPasswordMismatchError("Passwords do not match");
        return;
      }

      // Clear password mismatch error if passwords match
      setPasswordMismatchError('');

      setLoading(true);

      window.alert('Please use this new password for your next login.');
      
      const response = await GYAxios.patch('/keycloak/reset-password', {
        newPassword: password
      });

      if (response.data.success) {
        Noti.create({
          type: "s",
          msg: response.data.message
        });
        setVisible(false);
        form.resetFields();
        setPassword('');
        setConfirmPassword('');
        setPasswordMismatchError('');
      }
    } catch (error) {
      console.error('Password reset error:', error);
      Noti.create({
        type: "e",
        msg: "Something is wrong, please try reseting the password again"
      });
    } finally {
      setLoading(false);
    }
  };

  // const handleCancel = () => {
  //   setVisible(false);
  //   form.resetFields();
  //   setPassword('');
  //   setConfirmPassword('');
  //   setPasswordMismatchError('');
  // };

  const getPasswordStrengthIndicator = () => {
    if (!password) return null;
    
    const validation = validatePassword(password);
    return (
      <div style={{ marginTop: 8 }}>
        <Text type={validation.isValid ? "success" : "danger"} style={{ fontSize: '12px' }}>
          Password requirements:
        </Text>
        <ul style={{ margin: '4px 0', paddingLeft: '16px', fontSize: '12px' }}>
          <li style={{ color: password.length >= 8 ? 'green' : 'red' }}>
            At least 8 characters long
          </li>
          <li style={{ color: /[A-Z]/.test(password) ? 'green' : 'red' }}>
            Contains uppercase letter
          </li>
          <li style={{ color: /[a-z]/.test(password) ? 'green' : 'red' }}>
            Contains lowercase letter
          </li>
          <li style={{ color: /[0-9]/.test(password) ? 'green' : 'red' }}>
            Contains number
          </li>
          <li style={{ color: /[!@#$%^&*(),.?":{}|<>[\]\\\/;'+=_-]/.test(password) ? 'green' : 'red' }}>
            Contains special character (!@#$%^&*(),.?":{}|&lt;&gt;[]\/;'+=_-)
          </li>
          <li style={{ color: !/\s/.test(password) ? 'green' : 'red' }}>
            No spaces allowed
          </li>
        </ul>
      </div>
    );
  };

  return (
    <Modal
      title="Password Reset Required"
      open={visible}
      // onCancel={handleCancel}
      footer={null}
      closable={false}
      maskClosable={false}
      centered
      width={500}
    >
      <div style={{ marginBottom: 24 }}>
        <Text>
          According to the improvement of our security, your account needs to reset the password containing uppercase, lowercase, number, special character without spacing. The password should be at least 8 characters long.
        </Text>
      </div>

      <Form form={form} layout="vertical">
        <Form.Item
          label="New Password"
          name="password"
          rules={[
            { required: true, message: 'Please enter your new password' }
          ]}
        >
          <Input.Password
            placeholder="Enter new password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              // Clear error when user starts typing
              if (passwordMismatchError) {
                setPasswordMismatchError('');
              }
            }}
            iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
          />
        </Form.Item>

        {getPasswordStrengthIndicator()}

        <Form.Item
          label="Confirm New Password"
          name="confirmPassword"
          rules={[
            { required: true, message: 'Please confirm your new password' }
          ]}
          style={{ marginTop: 16 }}
        >
          <Input.Password
            placeholder="Confirm new password"
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              // Clear error when user starts typing
              if (passwordMismatchError) {
                setPasswordMismatchError('');
              }
            }}
            iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
          />
        </Form.Item>
        {passwordMismatchError && (
          <Text type="danger" style={{ fontSize: '12px', display: 'block', marginTop: '4px' }}>
            {passwordMismatchError}
          </Text>
        )}

        <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            {/* <Button onClick={handleCancel} disabled={loading}>
              Cancel
            </Button> */}
            <Button 
              type="primary" 
              onClick={handleResetPassword}
              loading={loading}
            >
              Reset Password
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default KCPassResetPopup;